import { formatDate } from '@progress/kendo-intl';
import { isEmpty } from 'lodash';
import moment from 'moment-timezone';
import { useCallback } from 'react';
import uuid from 'react-uuid';
import { api } from 'src/assets/static/ApiRoutes';
import useAsync from 'src/hooks/useAsync';
import axiosInstance from 'src/utils/axios';
import { removeTime } from 'src/utils/formatTime';
import { isEqualString } from 'src/utils/strings';
import { convertDate } from 'src/utils/transformation';

interface GLPeriodTransactionRequest {
  glCodeId?: number;
  glPeriodId: number;
}

export interface GLPeriodTransactionResponse {
  transactionDate: string;
  date?: Date;
  rowId: string;
  user: string;
  documentNumber: string;
  poId: number;
  action: string;
  total: number;
  items: [
    {
      lineItem: string;
      itemNum: string;
      description: string;
      amount: number;
    }
  ];
  glCode: string;
  budgetAmt: number;
  forecast: number;
  balance: number;
  endingBalance: number;
  invoiceNumber: string;
  invoiceId: number;
  notes: string;
  comments: string;
  poSubject: string;
}

const useGLPeriodTransactionApi = () => {
  const call = useCallback(
    (params?: GLPeriodTransactionRequest) =>
      new Promise<GLPeriodTransactionResponse[]>((resolve, reject) => {
        axiosInstance
          .get<GLPeriodTransactionResponse[]>(
            api.GLPeriodTransaction(params?.glPeriodId ?? 0, params?.glCodeId)
          )
          .then((data) => {
            const res = data.data.map((item) => {
              const date = convertDate(item.transactionDate);
              const isJournalEntry = isEqualString(item.action, 'journal entry');
              const commentsData = !isEmpty(item.comments) || item.comments !== null ? item.comments : item.poSubject
              const action =
                isJournalEntry && item.total <= 0
                  ? 'Journal Entry - Credit'
                  : isJournalEntry && item.total > 0
                  ? 'Journal Entry - Debit'
                  : item.action;

              return {
                ...item,
                rowId: uuid(),
                transactionDate: formatDate(date, 'MM/dd/yyyy h:mm:ss a'),
                _transactionDate: new Date(moment(date).startOf('day').format()),
                date: removeTime(date),
                endingBalance: item.forecast - item.balance,
                action: action,
                comments: commentsData
              };
            });
            return resolve(res);
          })
          .catch((error) => {
            reject(error);
          });
      }),
    []
  );

  const { execute, value, isLoading } = useAsync(
    call,
    false,
    undefined,
    [] as GLPeriodTransactionResponse[]
  );

  return {
    getGLPeriodTransactionLog: execute,
    glPeriodTransactionLog: value,
    isSpentLoading: isLoading,
  };
};

export default useGLPeriodTransactionApi;
